// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext({});
export const useAuth = () => useContext(AuthContext);

// ============================================================
// FRCC Events Auth Context
// ============================================================
const APP_NAME = 'filthyredneckcc';
const BRAND_KEY = 'frcc';
const DEFAULT_ROLE = 'attendee';
// ============================================================

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingStep, setOnboardingStep] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) fetchProfile(session.user);
      else setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session?.user) fetchProfile(session.user);
      else { setUser(null); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  async function fetchProfile(authUser) {
    const { data } = await supabase.from('profiles').select('*').eq('id', authUser.id).single();
    const profile = data ? { ...authUser, ...data } : { ...authUser, role: DEFAULT_ROLE };
    setUser(profile);
    const pwChanged = localStorage.getItem(`${BRAND_KEY}_password_changed`);
    const ndaSigned = authUser?.user_metadata?.nda_signed_at || profile.nda_signed_at || localStorage.getItem(`${BRAND_KEY}_nda_signed`);
    const tutorialDone = localStorage.getItem(`${BRAND_KEY}_tutorial_done`);
    if (!pwChanged) setOnboardingStep('password');
    else if (!ndaSigned) setOnboardingStep('nda');
    else if (!tutorialDone) setOnboardingStep('tutorial');
    else setOnboardingStep(null);
    setLoading(false);
  }

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    if (error) throw error;
    await supabase.from('profiles').update({ app_name: APP_NAME, last_login: new Date().toISOString() }).eq('id', (await supabase.auth.getUser()).data.user.id);
  }

  async function signUp(email, password, fullName) {
    const { data: authData, error } = await supabase.auth.signUp({ email: email.trim().toLowerCase(), password });
    if (error) throw error;
    if (authData.user) {
      await supabase.from('profiles').upsert({ id: authData.user.id, email, full_name: fullName, role: DEFAULT_ROLE, app_name: APP_NAME });
    }
  }

  const completeOnboardingStep = (step) => {
    if (step === 'password') { localStorage.setItem(`${BRAND_KEY}_password_changed`, 'true'); setOnboardingStep('nda'); }
    else if (step === 'nda') { localStorage.setItem(`${BRAND_KEY}_nda_signed`, 'true'); setOnboardingStep('tutorial'); }
    else if (step === 'tutorial') { localStorage.setItem(`${BRAND_KEY}_tutorial_done`, 'true'); setOnboardingStep(null); }
  };

  const demoBypass = () => {
    localStorage.setItem(`${BRAND_KEY}_password_changed`, 'true');
    localStorage.setItem(`${BRAND_KEY}_nda_signed`, 'true');
    localStorage.setItem(`${BRAND_KEY}_tutorial_done`, 'true');
    setUser({ email: 'rmiskulin@gmail.com', full_name: 'Ryan Miskulin', role: 'owner' });
    setOnboardingStep(null);
  };

  async function signOut() { await supabase.auth.signOut(); setUser(null); setOnboardingStep(null); }

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut, demoBypass, onboardingStep, completeOnboardingStep }}>
      {children}
    </AuthContext.Provider>
  );
}
